import { CircularProgress } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'styled-components';

import { deliveryFee } from '../../../../shared/env';
import {
  useRequestCdDelivery,
  useVerifyCdDeliveryPayment,
} from '../../../../shared/hooks/caseHook';
import { usePay } from '../../../../shared/hooks/paymentHook';
import { BlueRoundedButtonWithIcon } from '../../common/components/BlueRoundedButton';
import {
  CheckboxText,
  CheckboxWithText,
} from '../../common/components/CheckboxWithText';
import {
  AddresseeSlots,
  AddressForm,
  InvalidState,
} from '../../common/components/Slot/AddresseeSlots';
import { Spacer } from '../../common/components/Spacer';
import { SectionHeading } from '../../common/components/styledComponents';

import { PaymentConfirmModal } from './PaymentConfirmModal';
import { PaymentTable } from './PaymentTable';

const PaymentAlertMessage = styled.div`
  margin-top: 32px;
  color: ${p => (p.theme.isMobile ? '#a3a6ab' : '#7a7a7e')};
  font-size: 14px;
  line-height: 20px;
  text-align: center;
  word-break: keep-all;
`;
const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
`;

const initialInvalidState: InvalidState = {
  address: false,
  name: false,
  phone: false,
};
const initialPaymentForm = {
  address: { baseAddress: '', detailAddress: '', postalCode: '' },
  name: '',
  phone: '',
};
interface PaymentSectionProps {
  caseIds: string[];
  onClose: () => void;
}
export const PaymentSection = ({ caseIds, onClose }: PaymentSectionProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.imageList.cd.payment',
  });
  const [form, setForm] = useState<AddressForm>(initialPaymentForm);
  const [invalidState, setInvalidState] =
    useState<InvalidState>(initialInvalidState);
  const [checkTerm, setCheckTerm] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const { isPendingRequestCdDelivery, requestCdDeliveryPayment } =
    usePaymentSectionPay({
      paymentName: `${t('payment.name')}`,
      onSuccessVerify: () => setOpenModal(true),
    });

  const handleClickSubmitButton = () => {
    setInvalidState(() => initialInvalidState);
    const [invalidProperties, newInvalidState] =
      validatePaymentSectionForm(form);
    if (invalidProperties.length > 0) {
      setInvalidState(newInvalidState);
      return;
    }

    requestCdDeliveryPayment({
      caseIds,
      mailingAddress: {
        ...form.address,
        receiverName: form.name,
        receiverPhone: form.phone,
      },
      deliveryFee,
    });
  };

  const handleCloseConfirmModal = () => {
    onClose();
    setOpenModal(false);
  };

  return (
    <Container>
      <SectionHeading>{t('title')}</SectionHeading>
      <Spacer height="10px" />
      <PaymentTable />
      <AddresseeSlots
        invalidState={invalidState}
        onChangeForm={setForm}
        form={form}
      />
      <PaymentAlertMessage>{t('alert')}</PaymentAlertMessage>
      <CheckboxWithText
        text={<CheckboxText>{t('check')}</CheckboxText>}
        $margin="18px 0 0 0"
        value={checkTerm}
        onChange={setCheckTerm}
      />
      <BlueRoundedButtonWithIcon
        marginTop="30px"
        onClick={handleClickSubmitButton}
        disabled={!checkTerm}>
        {isPendingRequestCdDelivery ? (
          <CircularProgress sx={{ color: '#ffffff' }} />
        ) : (
          t('button')
        )}
      </BlueRoundedButtonWithIcon>
      <PaymentConfirmModal open={openModal} onClose={handleCloseConfirmModal} />
    </Container>
  );
};

const validatePaymentSectionForm = (
  form: AddressForm,
): [string[], InvalidState] => {
  const newInvalidState = { ...initialInvalidState };
  const invalidProperties = [];
  if (form.name === '') {
    newInvalidState.name = true;
    invalidProperties.push('receiver.name');
  }
  if (form.phone === '') {
    newInvalidState.phone = true;
    invalidProperties.push('receiver.phone');
  }
  if (
    form.address.baseAddress === '' ||
    form.address.detailAddress === '' ||
    form.address.postalCode === ''
  ) {
    newInvalidState.address = true;
    invalidProperties.push('receiver.address');
  }
  return [invalidProperties, newInvalidState];
};

interface PaymentSectionPayProps {
  paymentName: string;
  onSuccessVerify: () => void;
}
const usePaymentSectionPay = ({
  paymentName,
  onSuccessVerify,
}: PaymentSectionPayProps) => {
  const { mutate: verifyPayment } = useVerifyCdDeliveryPayment({
    onSuccess: onSuccessVerify,
  });
  const { pay } = usePay({
    paymentName,
    onSuccessPay: verifyPayment,
    mobileRedirectUrlPath: '/mobile-payment-callback/cd',
  });
  const {
    mutate: requestCdDeliveryPayment,
    isPending: isPendingRequestCdDelivery,
  } = useRequestCdDelivery({
    onSuccess(data) {
      const merchantId = data.id;
      if (merchantId) {
        pay(merchantId, data.price.amount);
      }
    },
  });

  return {
    isPendingRequestCdDelivery,
    requestCdDeliveryPayment,
  };
};
